import React, { Component } from 'react';
import moment from 'moment/min/moment-with-locales';
import socketIOClient from 'socket.io-client';
import agent from '~/library/agent';
import { serverValidation } from '~/library/core';
import { SOCKET_ROOT, getUploadedFileURL } from '~/library/config';
import i18n from '~/i18n';

import { shortDateFormat, dateToTimeFormat } from '~/utils/dateFormat';
import { Input } from '../../../elements/Input';
import Select from '../../../elements/Select';
import SwitchWidget from '../ControlPanel/SwitchWidget';
import RequiredStar from '../../../elements/RequiredStar';
import CustomCheckbox from '../../../components/CustomCheckbox/CustomCheckbox';

import newLogo from '../../../assets/img/new-logo.svg';
import hygieneZoneIcon from '../../../assets/img/hygiene-zone.svg';
import impureZoneIcon from '../../../assets/img/impure-zone.svg';
import healthSafetyIcon from '../../../assets/img/health-safety-icon.svg';

import ukFlag from '../../../assets/img/new_design/jsx_adminnavbar_svg/uk_flag.png';
import norwayFlag from '../../../assets/img/new_design/jsx_adminnavbar_svg/norway_flag.png';
import polandFlag from '../../../assets/img/new_design/jsx_adminnavbar_svg/poland_flag.png';
import lithuaniaFlag from '../../../assets/img/new_design/jsx_adminnavbar_svg/lithuania_flag.png';
import russianFlag from '../../../assets/img/new_design/jsx_adminnavbar_svg/russian_flag.png';
import spainFlag from '../../../assets/img/new_design/jsx_adminnavbar_svg/spain_flag.png';
import greekFlag from '../../../assets/img/new_design/jsx_adminnavbar_svg/greek_flag.png';

import '../../../styles/scss/visitor.scss';

const LANGUAGE_OPTIONS = [
    { code: 'en', label: 'English',    flag: ukFlag },
    { code: 'no', label: 'Norsk',      flag: norwayFlag },
    { code: 'pl', label: 'Polish',     flag: polandFlag },
    { code: 'lt', label: 'Lithuanian', flag: lithuaniaFlag },
    { code: 'ru', label: 'Russian',    flag: russianFlag },
    { code: 'es', label: 'Spanish',    flag: spainFlag },
    { code: 'gr', label: 'Greek',      flag: greekFlag },
];

const STEP_LANDING = 0;
const STEP_ZONE = 1;
const STEP_FORM = 2;
const STEP_HEALTH = 3;
const STEP_CONFIRM = 4;

const PURPOSE_OPTIONS = [
    'Meeting', 'Delivery', 'Maintenance', 'Inspection', 'Interview', 'Other',
].map(p => ({ value: p, label: p }));

const ZONE_ICONS = { hygiene: hygieneZoneIcon, general: impureZoneIcon };

function ZoneIcon({ type }) {
    if (ZONE_ICONS[type]) {
        return <img src={ZONE_ICONS[type]} alt={type} width={44} height={44} className="vpr-zone-img" />;
    }
    if (type === 'restricted') {
        return (
            <div className="vpr-zone-icon-box">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
            </div>
        );
    }
    return (
        <div className="vpr-zone-icon-box">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 8C8 10 5.9 16.17 3.82 22"/>
                <path d="M9.5 5.5a7 7 0 0 1 8.5 8.5"/>
                <path d="M12 2a10 10 0 0 1 8 16"/>
            </svg>
        </div>
    );
}

class VisitorPublicRegister extends Component {
    constructor(props) {
        super(props);
        const enc = this.getEnc();
        this.state = {
            enc,
            language: sessionStorage.getItem('locale') || 'en',
            step: STEP_LANDING,
            loading: true,
            error: '',
            fieldErrors: {},
            submitted: false,
            clientInfo: null,
            zones: [],
            pmEmployees: [],
            safetyRules: [],
            safetyAnswers: {},
            selectedZone: null,
            visitor_name: '',
            visitor_company: '',
            visitor_phone: '',
            visitor_email: '',
            contact_person_id: null,
            contact_person_name: '',
            purpose_of_visit: PURPOSE_OPTIONS[0],
            visit_date: moment().format('YYYY-MM-DD'),
            visit_end_date: moment().format('YYYY-MM-DD'),
            visit_time: moment().format('HH:mm'),
            result: null,
            polledLog: null,
            submitting: false,
            declared: false,
            langDropdownOpen: false,
            qrDataUrl: null,
            passQrDataUrl: null,
        };
    }

    getEnc() {
        const path = window.location.pathname;
        const parts = path.split('/visitorRegister/');
        return parts.length > 1 ? parts[1] : null;
    }

    t = (key, opts) => i18n.t(key, opts);

    changeLanguage = (code) => {
        i18n.changeLanguage(code);
        sessionStorage.setItem('locale', code);
        this.setState({ language: code, langDropdownOpen: false });
    };

    _closeLangDropdown = (e) => {
        if (this._langRef && !this._langRef.contains(e.target)) {
            this.setState({ langDropdownOpen: false });
        }
    };

    componentDidMount() {
        document.addEventListener('mousedown', this._closeLangDropdown);
        i18n.changeLanguage(this.state.language);
        this._loadData();
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.step === STEP_CONFIRM && prevState.step !== STEP_CONFIRM) {
            const log = this.state.result && this.state.result.log;
            if (log && log.status === 'pending') this._connectSocket(log.id);
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this._closeLangDropdown);
        this._disconnectSocket();
        sessionStorage.removeItem('visitorSessionToken');
    }

    _connectSocket = (logId) => {
        this._disconnectSocket();
        const socket = socketIOClient(SOCKET_ROOT);
        this._socket = socket;
        socket.on('connect', () => {
            socket.emit('join-visitor-log', logId);
        });
        socket.on('visitor-log-status', (data) => {
            if (data && data.id === logId) {
                this.setState(prev => {
                    const base = prev.polledLog || (prev.result && prev.result.log) || {};
                    return { polledLog: { ...base, status: data.status } };
                });
                if (data.status !== 'pending') this._disconnectSocket();
            }
        });
    };

    _disconnectSocket = () => {
        if (this._socket) { this._socket.disconnect(); this._socket = null; }
    };

    handleDownloadPublicPass = () => {
        const { result, polledLog } = this.state;
        const log = polledLog || (result && result.log);
        if (!log) return;
        window.open(`${agent.config.API_ROOT}/visitors/public/log/${log.id}/pass`, '_blank');
    };

    renderPage(cardContent, cardExtraClass = '') {
        return (
            <div className="vpr-page">
                <div className="vpr-lang-bar">
                    {this.renderLanguageDropdown()}
                </div>
                <div className={`vpr-card${cardExtraClass ? ' ' + cardExtraClass : ''}`}>
                    {cardContent}
                </div>
            </div>
        );
    }

    renderLanguageDropdown() {
        const { language, langDropdownOpen } = this.state;
        const current = LANGUAGE_OPTIONS.find(l => l.code === language) || LANGUAGE_OPTIONS[0];
        return (
            <div ref={r => { this._langRef = r; }} className="vpr-lang-wrapper">
                <button
                    onClick={() => this.setState(prev => ({ langDropdownOpen: !prev.langDropdownOpen }))}
                    className="vpr-lang-btn"
                >
                    <img src={current.flag} alt={current.label} width={20} height={14} className="vpr-flag-img" />
                    {current.label}
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"/>
                    </svg>
                </button>
                {langDropdownOpen && (
                    <div className="vpr-lang-dropdown">
                        {LANGUAGE_OPTIONS.map(lang => (
                            <button
                                key={lang.code}
                                onClick={() => this.changeLanguage(lang.code)}
                                className={`vpr-lang-option${language === lang.code ? ' vpr-lang-option--active' : ''}`}
                            >
                                <img src={lang.flag} alt={lang.label} width={20} height={14} className="vpr-flag-img" />
                                {lang.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    _loadData = async () => {
        const { enc } = this.state;
        if (!enc) {
            this.setState({ loading: false, error: i18n.t('Invalid registration link') });
            return;
        }
        try {
            const zonesData = await agent.visitors.getPublicZones(enc);
            if (zonesData.sessionToken) {
                sessionStorage.setItem('visitorSessionToken', zonesData.sessionToken);
            }
            const [pmData, qrData] = await Promise.all([
                agent.visitors.getPublicPmEmployees(enc),
                agent.visitors.getPublicRegistrationQr(enc).catch(() => ({})),
            ]);
            this.setState({
                loading: false,
                clientInfo: zonesData.client,
                zones: zonesData.zones || [],
                pmEmployees: pmData.employees || [],
                qrDataUrl: qrData.qrDataUrl || null,
            });
        } catch (e) {
            this.setState({ loading: false, error: i18n.t('Failed to load registration form.') });
        }
    };

    _employeeSearchTimer = null;

    loadEmployeeOptions = (inputValue, callback) => {
        clearTimeout(this._employeeSearchTimer);
        if (!inputValue || inputValue.length < 3) {
            callback([]);
            return;
        }
        this._employeeSearchTimer = setTimeout(async () => {
            try {
                const data = await agent.visitors.searchPublicEmployees(this.state.enc, inputValue);
                const opts = (data.employees || []).map(e => ({
                    value: e.id,
                    label: `${e.first_name} ${e.last_name}`,
                }));
                callback(opts);
            } catch (e) {
                callback([]);
            }
        }, 300);
    };

    selectZone = async (zone) => {
        const { enc } = this.state;
        this.setState({ selectedZone: zone });
        try {
            const data = await agent.visitors.getPublicSafetyRules(enc, zone.id);
            const rules = data.rules || [];
            const safetyAnswers = {};
            rules.forEach(r => { safetyAnswers[r.id] = null; });
            this.setState({ safetyRules: rules, safetyAnswers });
        } catch (e) {
            if (this._isSessionError(e)) { this._onSessionExpired(); return; }
            this.setState({ safetyRules: [] });
        }
    };

    handleZoneNext = () => {
        if (!this.state.selectedZone) {
            this.setState({ error: i18n.t('Please select a zone to continue') });
            return;
        }
        this.setState({ error: '', step: STEP_FORM, visit_time: moment().format('HH:mm') });
    };

    validateFormFields = (state) => {
        const { visitor_name, visitor_company, visitor_phone, visitor_email, contact_person_id, safetyRules, declared } = state || this.state;
        const fieldErrors = {};
        if (!visitor_name.trim()) fieldErrors.visitor_name = i18n.t('Cannot be blank');
        if (!visitor_company.trim()) fieldErrors.visitor_company = i18n.t('Cannot be blank');
        if (!visitor_phone.trim()) {
            fieldErrors.visitor_phone = i18n.t('Cannot be blank');
        } else if (!visitor_phone.trim().match(serverValidation('phone'))) {
            fieldErrors.visitor_phone = i18n.t('Should match format "phone"');
        }
        if (!visitor_email.trim()) {
            fieldErrors.visitor_email = i18n.t('Cannot be blank');
        } else if (!visitor_email.trim().match(serverValidation('email'))) {
            fieldErrors.visitor_email = i18n.t('Should match format "email"');
        }
        if (!contact_person_id) fieldErrors.contact_person_id = i18n.t('Cannot be blank');
        const healthRules = safetyRules.filter(r => r.category === 'health');
        if (healthRules.length > 0 && !declared) fieldErrors.declared = i18n.t('Please check the declaration to continue');
        return fieldErrors;
    };

    handleFormConfirm = async () => {
        const fieldErrors = this.validateFormFields();
        if (Object.keys(fieldErrors).length > 0) {
            this.setState({ fieldErrors, submitted: true, error: '' });
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        this.setState({ fieldErrors: {}, submitted: true, error: '', submitting: true });
        const { enc, visitor_email, visitor_phone, visit_date } = this.state;
        try {
            const res = await agent.visitors.checkPublicDuplicate(enc, visitor_email, visitor_phone, visit_date);
            if (res && res.duplicate) {
                this.setState({ submitting: false, error: res.message });
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        } catch (e) {
            if (this._isSessionError(e)) {
                this.setState({ submitting: false });
                this._onSessionExpired();
                return;
            }
        }
        this.setState({ submitting: false });
        const otherRules = this.state.safetyRules.filter(r => r.category !== 'health');
        if (otherRules.length > 0) {
            this.setState({ step: STEP_HEALTH });
        } else {
            this.submitRegistration();
        }
    };

    handleHealthContinue = () => {
        const { safetyRules, safetyAnswers } = this.state;
        const otherRules = safetyRules.filter(r => r.category !== 'health');
        for (const rule of otherRules) {
            if (rule.is_mandatory && !safetyAnswers[rule.id]) {
                this.setState({ error: `Please answer: "${rule.question}"` });
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        }
        this.setState({ error: '' });
        this.submitRegistration();
    };

    submitRegistration = async () => {
        const {
            enc, selectedZone, visitor_name, visitor_company, visitor_phone,
            visitor_email, contact_person_id, contact_person_name,
            purpose_of_visit, visit_date, visit_end_date, visit_time, safetyRules, safetyAnswers,
        } = this.state;
        this.setState({ submitting: true, error: '' });
        const check_in_time = `${visit_date}T${visit_time}`;
        const check_out_date = visit_end_date;
        const safety_responses = safetyRules
            .map(r => ({ rule_id: r.id, answer: safetyAnswers[r.id] || 'no' }));
        try {
            const result = await agent.visitors.publicRegister(enc, {
                zone_id: selectedZone ? selectedZone.id : null,
                visitor_name, visitor_company, visitor_phone, visitor_email,
                contact_person_id, contact_person_name,
                purpose_of_visit: purpose_of_visit ? purpose_of_visit.value : '',
                check_in_time, check_out_date, safety_responses,
                language: this.state.language,
            });
            this.setState({ result, passQrDataUrl: result.passQrDataUrl || null, submitting: false, step: STEP_CONFIRM });
        } catch (e) {
            if (this._isSessionError(e)) {
                this.setState({ submitting: false });
                this._onSessionExpired();
                return;
            }
            const serverMsg = e && e.response && e.response.body && e.response.body.error;
            this.setState({ submitting: false, error: serverMsg || i18n.t('Submission failed. Please try again.') });
        }
    };

    resetForm = () => {
        this._disconnectSocket();
        sessionStorage.removeItem('visitorSessionToken');
        this.setState({
            step: STEP_LANDING, result: null, polledLog: null, selectedZone: null, passQrDataUrl: null,
            safetyRules: [], safetyAnswers: {},
            visitor_name: '', visitor_company: '', visitor_phone: '', visitor_email: '',
            contact_person_id: null, contact_person_name: '',
            purpose_of_visit: PURPOSE_OPTIONS[0],
            visit_date: moment().format('YYYY-MM-DD'),
            visit_end_date: moment().format('YYYY-MM-DD'),
            visit_time: moment().format('HH:mm'),
            error: '',
            fieldErrors: {},
            submitted: false,
            declared: false,
        });
        this._loadData();
    };

    _isSessionError = (e) => {
        const status = (e && e.status) || (e && e.response && e.response.status);
        return status === 401;
    };

    _onSessionExpired = () => {
        this.setState({ error: i18n.t('Your session has expired. Please refresh the page and try again.') });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    setAnswer = (ruleId, val) => {
        this.setState(prev => ({ safetyAnswers: { ...prev.safetyAnswers, [ruleId]: val } }));
    };

    // ─── STEP 0: Landing ─────────────────────────────────────────────────────

    renderLandingStep() {
        const { qrDataUrl } = this.state;
        return this.renderPage(
            <>
                <div className="vpr-qr-box">
                    {qrDataUrl && <img src={qrDataUrl} alt="QR Code" width={140} height={140} />}
                </div>
                <div className="vpr-scan-hint">{this.t('Scan QR code')}</div>

                <button
                    className="vpr-primary-btn vpr-primary-btn--lg"
                    onClick={() => this.setState({ step: STEP_ZONE })}
                >
                    {this.t('Click Here to Register')}
                </button>

                <div className="vpr-powered-by">
                    {this.t('Powered by Time and Control')}
                    <img src={newLogo} alt="Time and Control" height={30} style={{ opacity: 0.7 }} />
                </div>
            </>,
            'vpr-card--center'
        );
    }

    // ─── STEP 1: Zone Selection ───────────────────────────────────────────────

    renderZoneStep() {
        const { zones, clientInfo, selectedZone, error, loading } = this.state;
        return this.renderPage(
            <>
                <div className="vpr-page-title">{this.t('Visitor Entry')}</div>

                {clientInfo && clientInfo.logo_id && (
                    <div className="vpr-client-logo">
                        <img src={getUploadedFileURL(clientInfo.logo_id)} alt={clientInfo.name}
                            style={{ maxHeight: 70, maxWidth: '80%', objectFit: 'contain' }} />
                    </div>
                )}
                {clientInfo && !clientInfo.logo_id && (
                    <div className="vpr-client-logo">
                        <div className="vpr-client-badge">{clientInfo.name}</div>
                    </div>
                )}

                <div className="vpr-section-title">{this.t('Select Zone')}</div>
                <div className="vpr-subtitle vpr-subtitle--center">{this.t('Which area of the facility are you entering?')}</div>

                {error && <div className="vpr-error-box">{error}</div>}

                {loading ? (
                    <div className="vpr-loading">{this.t('Loading...')}</div>
                ) : zones.length === 0 ? (
                    <div className="vpr-loading">{this.t('No zones configured. Please contact reception.')}</div>
                ) : (
                    <div className="vpr-zones-list">
                        {zones.map(zone => {
                            const isSelected = selectedZone && selectedZone.id === zone.id;
                            return (
                                <div
                                    key={zone.id}
                                    className={`vpr-zone-card${isSelected ? ' vpr-zone-card--selected' : ''}`}
                                    onClick={() => this.selectZone(zone)}
                                >
                                    <ZoneIcon type={zone.zone_type} />
                                    <div className="vpr-zone-text-block">
                                        <div className="vpr-zone-name">{zone.name}</div>
                                        {zone.description && <div className="vpr-zone-desc">{zone.description}</div>}
                                        {zone.requirements && (
                                            <div className={zone.zone_type === 'hygiene' ? 'vpr-req-green' : 'vpr-req-amber'}>
                                                {zone.zone_type === 'hygiene' ? (
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="vpr-req-icon">
                                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                                                    </svg>
                                                ) : (
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="vpr-req-icon">
                                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                                        <line x1="12" y1="9" x2="12" y2="13"/>
                                                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                                                    </svg>
                                                )}
                                                {zone.requirements}
                                            </div>
                                        )}
                                    </div>
                                    {isSelected && (
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2769f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                            <polyline points="22 4 12 14.01 9 11.01"/>
                                        </svg>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="vpr-safety-notice">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2769f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="vpr-safety-notice__icon">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <div>
                        <div className="vpr-safety-notice__title">{this.t('Safety Notice')}</div>
                        <div className="vpr-safety-notice__text">
                            {this.t('Changing zones requires passing through a hygiene checkpoint. Ensure you have the correct PPE for the selected zone.')}
                        </div>
                    </div>
                </div>

                <button className="vpr-primary-btn" onClick={this.handleZoneNext}>{this.t('Next')}</button>
            </>
        );
    }

    // ─── STEP 2: Registration Form ────────────────────────────────────────────

    renderFormStep() {
        const {
            selectedZone, visitor_name, visitor_company, visitor_phone, visitor_email,
            contact_person_id, contact_person_name, purpose_of_visit, visit_date, visit_end_date, visit_time,
            safetyRules, safetyAnswers, declared, error, fieldErrors, submitted, submitting,
        } = this.state;
        const healthRules = safetyRules.filter(r => r.category === 'health');
        const confirmEnabled = healthRules.length === 0 || declared;

        const title = selectedZone ? `${selectedZone.name} Entry` : 'Visitor Registration';

        const selectedEmployee = contact_person_id
            ? { value: contact_person_id, label: contact_person_name }
            : null;

        const fe = fieldErrors;

        const revalidate = (patch) => {
            if (!submitted) return patch;
            const next = { ...this.state, ...patch };
            return { ...patch, fieldErrors: this.validateFormFields(next) };
        };

        return this.renderPage(
            <>
                <div className="vpr-nav-bar">
                    <button className="vpr-back-btn" onClick={() => this.setState({ step: STEP_ZONE, error: '', fieldErrors: {} })}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"/>
                        </svg>
                    </button>
                    <div className="vpr-nav-title">{this.t('Visitor Registration')}</div>
                    <div style={{ width: 28 }} />
                </div>

                <div className="vpr-form-title">{title}</div>
                <div className="vpr-subtitle">{this.t('Please complete the form below to register for entry into the high-hygiene area.')}</div>

                {error && <div className="vpr-error-box">{error}</div>}

                <div className="visitor-reg-form">
                    {/* Personal Information */}
                    <div className="vpr-section-label">{this.t('PERSONAL INFORMATION')}</div>

                    <div className="form-group field field-string w-100">
                        <label className={`control-label fs15${fe.visitor_name ? ' vpr-label-error' : ''}`}>{this.t('Visitor Name')}<RequiredStar /></label>
                        <Input value={visitor_name} onChange={e => this.setState(revalidate({ visitor_name: e.target.value }))} error={!!fe.visitor_name} maxLength={100} />
                        {fe.visitor_name && <div className="field-error-message"><span className="field-error-dot">•</span>{fe.visitor_name}</div>}
                    </div>

                    <div className="form-group field field-string w-100">
                        <label className={`control-label fs15${fe.visitor_phone ? ' vpr-label-error' : ''}`}>{this.t('Mobile Number')}<RequiredStar /></label>
                        <Input value={visitor_phone} onChange={e => this.setState(revalidate({ visitor_phone: e.target.value }))} type="tel" error={!!fe.visitor_phone} maxLength={20} />
                        {fe.visitor_phone && <div className="field-error-message"><span className="field-error-dot">•</span>{fe.visitor_phone}</div>}
                    </div>

                    <div className="form-group field field-string w-100">
                        <label className={`control-label fs15${fe.visitor_email ? ' vpr-label-error' : ''}`}>{this.t('Email Address')}<RequiredStar /></label>
                        <Input value={visitor_email} onChange={e => this.setState(revalidate({ visitor_email: e.target.value }))} type="email" error={!!fe.visitor_email} maxLength={150} />
                        {fe.visitor_email && <div className="field-error-message"><span className="field-error-dot">•</span>{fe.visitor_email}</div>}
                    </div>

                    <div className="form-group field field-string w-100">
                        <label className={`control-label fs15${fe.visitor_company ? ' vpr-label-error' : ''}`}>{this.t('Company Name')}<RequiredStar /></label>
                        <Input value={visitor_company} onChange={e => this.setState(revalidate({ visitor_company: e.target.value }))} error={!!fe.visitor_company} maxLength={100} />
                        {fe.visitor_company && <div className="field-error-message"><span className="field-error-dot">•</span>{fe.visitor_company}</div>}
                    </div>

                    {/* Visit Details */}
                    <div className="vpr-section-label vpr-section-label--mt">{this.t('VISIT DETAILS')}</div>

                    <div className="form-group field field-string w-100">
                        <label className={`control-label fs15${fe.contact_person_id ? ' vpr-label-error' : ''}`}>{this.t('Contact Person / Host')}<RequiredStar /></label>
                        <Select
                            asyncSelect
                            loadOptions={this.loadEmployeeOptions}
                            value={selectedEmployee}
                            onChange={opt => this.setState(revalidate({
                                contact_person_id: opt ? opt.value : null,
                                contact_person_name: opt ? opt.label : '',
                            }))}
                            placeholder={this.t('Type at least 3 characters...')}
                            isClearable
                            noOptionsMessage={({ inputValue }) =>
                                inputValue && inputValue.length >= 3
                                    ? this.t('No employees found')
                                    : this.t('Type at least 3 characters to search')
                            }
                        />
                        {fe.contact_person_id && <div className="field-error-message"><span className="field-error-dot">•</span>{fe.contact_person_id}</div>}
                    </div>

                    <div className="form-group field field-string w-100">
                        <label className="control-label fs15">{this.t('Purpose of Visit')}</label>
                        <Select
                            options={PURPOSE_OPTIONS}
                            value={purpose_of_visit}
                            onChange={opt => this.setState({ purpose_of_visit: opt })}
                        />
                    </div>

                    <div className="row">
                        <div className="col-6">
                            <div className="form-group field field-string w-100">
                                <label className="control-label fs15">{this.t('Start Date')}</label>
                                <Input
                                    value={visit_date}
                                    onChange={e => {
                                        const newStart = e.target.value;
                                        const maxEnd = moment(newStart).add(7, 'days').format('YYYY-MM-DD');
                                        let newEnd = visit_end_date;
                                        if (!newEnd || newEnd < newStart) newEnd = newStart;
                                        if (newEnd > maxEnd) newEnd = maxEnd;
                                        this.setState({ visit_date: newStart, visit_end_date: newEnd });
                                    }}
                                    type="date"
                                    min={moment().format('YYYY-MM-DD')}
                                />
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="form-group field field-string w-100">
                                <label className="control-label fs15">{this.t('End Date')}</label>
                                <Input
                                    value={visit_end_date}
                                    onChange={e => this.setState({ visit_end_date: e.target.value })}
                                    type="date"
                                    min={visit_date}
                                    max={moment(visit_date).add(7, 'days').format('YYYY-MM-DD')}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <div className="form-group field field-string w-100">
                                <label className="control-label fs15">{this.t('Time In')}</label>
                                <Input
                                    value={visit_time}
                                    onChange={e => this.setState({ visit_time: e.target.value })}
                                    type="time"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Inline Health Declaration */}
                    {healthRules.length > 0 && (
                        <div className={`vpr-health-box${fe.declared ? ' vpr-health-box--error' : ''}`}>
                            <div className="vpr-health-box-header">
                                <img src={healthSafetyIcon} alt="health" width={40} height={40} style={{ flexShrink: 0 }} />
                                <div style={{ marginLeft: 12 }}>
                                    <div className="vpr-health-title">{this.t('Health Declaration')}</div>
                                    <div className="vpr-health-zone-label">
                                        {selectedZone ? `${this.t('MANDATORY FOR ZONE ENTRY').replace('ZONE', selectedZone.name.toUpperCase())}` : this.t('MANDATORY FOR ZONE ENTRY')}
                                    </div>
                                </div>
                            </div>

                            {healthRules.map((rule, idx) => (
                                <div key={rule.id}>
                                    {idx > 0 && <hr className="vpr-health-divider" />}
                                    <div className="vpr-question-row">
                                        <span className="vpr-question-text">{rule.question}</span>
                                        <div style={{ flexShrink: 0 }}>
                                            <SwitchWidget
                                                value={safetyAnswers[rule.id] === 'yes'}
                                                onChange={val => this.setAnswer(rule.id, val ? 'yes' : 'no')}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <hr className="vpr-health-divider" style={{ marginBottom: 10 }} />
                            <CustomCheckbox
                                isChecked={declared}
                                onChange={val => {
                                    const scrollY = window.scrollY;
                                    this.setState(revalidate({ declared: val }), () => {
                                        window.scrollTo({ top: scrollY, behavior: 'instant' });
                                    });
                                }}
                                label={this.t('I hereby declare that the information provided above is true and I agree to follow all safety protocols of the {{zoneName}}.', { zoneName: selectedZone ? selectedZone.name : 'Zone' })}
                                wrapperClassName="declaration-checkbox mb-0"
                                labelClassName="fw-normal"
                            />
                            {fe.declared && <div className="field-error-message mt-1"><span className="field-error-dot">•</span>{fe.declared}</div>}
                        </div>
                    )}
                </div>{/* end visitor-reg-form */}

                <div className="vpr-form-footer">
                    <button
                        className={`vpr-primary-btn${!confirmEnabled || submitting ? ' vpr-primary-btn--disabled' : ''}${submitting ? ' vpr-primary-btn--submitting' : ''}`}
                        onClick={confirmEnabled && !submitting ? this.handleFormConfirm : undefined}
                        disabled={!confirmEnabled || submitting}
                    >
                        {submitting ? this.t('Submitting...') : this.t('Confirm')}
                    </button>
                    <button className="vpr-cancel-link" onClick={this.resetForm}>{this.t('Cancel Entry')}</button>
                </div>
            </>
        );
    }

    // ─── STEP 3: Full Health Declaration ─────────────────────────────────────

    renderHealthStep() {
        const { safetyRules, safetyAnswers, error, submitting } = this.state;
        const otherRules = safetyRules.filter(r => r.category !== 'health');
        return this.renderPage(
            <>
                <div className="vpr-nav-bar">
                    <button className="vpr-back-btn" onClick={() => this.setState({ step: STEP_FORM, error: '' })}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"/>
                        </svg>
                    </button>
                    <div className="vpr-nav-title">{this.t('Safety & Security Check')}</div>
                    <div style={{ width: 28 }} />
                </div>

                <div className="vpr-form-title">{this.t('Safety and Security Check')}</div>
                <div className="vpr-subtitle">{this.t('Please answer the following questions truthfully. False declarations may result in denied entry to the facility.')}</div>

                {error && <div className="vpr-error-box">{error}</div>}

                {otherRules.map((rule, idx) => {
                    const isText = rule.question_type === 'text';
                    const answerVal = safetyAnswers[rule.id] || '';
                    const MAX_ANSWER = 500;
                    return (
                        <div key={rule.id} className="vpr-rule-item">
                            <div className="vpr-rule-num">
                                {idx + 1}. {rule.question}
                            </div>
                            {isText ? (
                                <div className="vpr-text-answer-wrap">
                                    <textarea
                                        className="vpr-text-answer"
                                        rows={3}
                                        maxLength={MAX_ANSWER}
                                        value={answerVal}
                                        onChange={e => this.setAnswer(rule.id, e.target.value)}
                                        placeholder={this.t('Type your answer here...')}
                                    />
                                    <div className={`vpr-char-count${answerVal.length >= MAX_ANSWER ? ' vpr-char-count--limit' : ''}`}>
                                        {answerVal.length}/{MAX_ANSWER}
                                    </div>
                                </div>
                            ) : (
                                <div className="vpr-yes-no-row">
                                    {['yes', 'no'].map(ans => (
                                        <button
                                            key={ans}
                                            className={`vpr-yes-no-btn${safetyAnswers[rule.id] === ans ? ' vpr-yes-no-btn--active' : ''}`}
                                            onClick={() => this.setAnswer(rule.id, safetyAnswers[rule.id] === ans ? null : ans)}
                                        >
                                            {ans.charAt(0).toUpperCase() + ans.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}

                <div className="vpr-certify-text">
                    {this.t('By submitting, you certify that all answers are true. False information may lead to legal action or permanent ban from the premises.')}
                </div>

                <button
                    className={`vpr-primary-btn${submitting ? ' vpr-primary-btn--submitting' : ''}`}
                    onClick={this.handleHealthContinue}
                    disabled={submitting}
                >
                    {submitting ? this.t('Submitting...') : this.t('Continue')}
                </button>
                <button className="vpr-cancel-link" onClick={this.resetForm}>{this.t('Cancel Entry')}</button>
            </>
        );
    }

    // ─── STEP 4: Confirmation ─────────────────────────────────────────────────

    renderConfirmStep() {
        const { result, polledLog, visitor_name, visitor_company, visit_date, visit_end_date, visit_time, clientInfo, passQrDataUrl } = this.state;
        const dateTimeRules = clientInfo && clientInfo.dateTimeRules;
        const log = polledLog || (result && result.log) || {};
        const status = log.status || 'pending';
        const isApproved = status === 'approved';
        const isRejected = status === 'rejected';

        const initials = visitor_name
            ? visitor_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
            : 'V';
        const refNum = log.id
            ? `#${String(log.id).padStart(4, '0')}-${log.id.toString(16).toUpperCase().padStart(2, '0')}`
            : '#0000';
        const checkInMoment = log.check_in_time
            ? moment(log.check_in_time)
            : moment(`${visit_date}T${visit_time}`);
        const checkOutMoment = log.check_out_time
            ? moment(log.check_out_time)
            : moment(visit_end_date || visit_date).endOf('day');
        const isMultiDay = !checkInMoment.isSame(checkOutMoment, 'day');

        const statusBadgeStyle = {
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
            background: isApproved ? '#d1fae5' : isRejected ? '#fef2f2' : '#fff7ed',
            color: isApproved ? '#15803d' : isRejected ? '#dc2626' : '#b45309',
            border: `1px solid ${isApproved ? '#22c55e' : isRejected ? '#ef4444' : '#f59e0b'}`,
        };

        return this.renderPage(
            <>
                <button className="vpr-back-btn vpr-confirm-close" onClick={this.resetForm}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>
                <div className="vpr-confirm-info-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2769f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                </div>

                <div className="vpr-confirm-wrap">
                    <div className="vpr-nav-title">{this.t('Confirmation')}</div>

                    {clientInfo && (
                        <div className="vpr-confirm-branding">
                            {clientInfo.logo_id
                                ? <img src={getUploadedFileURL(clientInfo.logo_id)} alt={clientInfo.name} style={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain' }} />
                                : <div className="vpr-confirm-client-name">{clientInfo.name}</div>
                            }
                            {clientInfo.logo_id && clientInfo.name && (
                                <div className="vpr-confirm-client-sub">{clientInfo.name}</div>
                            )}
                        </div>
                    )}

                    <div className={`vpr-success-circle${isRejected ? ' vpr-success-circle--rejected' : ''}`}>
                        <div className={`vpr-success-circle-inner${isRejected ? ' vpr-success-circle-inner--rejected' : ''}`}>
                            {isRejected ? (
                                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            ) : (
                                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                </svg>
                            )}
                        </div>
                    </div>

                    <div className={`vpr-confirm-title${isRejected ? ' vpr-confirm-title--rejected' : ''}`}>
                        {isRejected ? this.t('Entry Rejected') : this.t('Entry Submitted Successfully')}
                    </div>
                    <div className="vpr-confirm-desc">
                        {isApproved
                            ? this.t('Your entry has been approved. You are safe to enter the facility.')
                            : isRejected
                            ? this.t('Your entry has been rejected. Please contact the reception for assistance.')
                            : this.t('Your entry is pending approval. This page will update automatically.')}
                    </div>

                    <div className="vpr-status-row">
                        <span className="vpr-status-row-label">{this.t('STATUS')}</span>
                        <span style={statusBadgeStyle}>
                            <i className={`fas ${isApproved ? 'fa-shield-alt' : isRejected ? 'fa-times-circle' : 'fa-clock'}`} style={{ fontSize: 11 }} />
                            {isApproved ? this.t('APPROVED') : isRejected ? this.t('REJECTED') : this.t('PENDING')}
                        </span>
                    </div>

                    <div className="vpr-visitor-block">
                        <div className="vpr-visitor-avatar">{initials}</div>
                        <div className="vpr-visitor-name">{visitor_name}</div>
                        <div className="vpr-visitor-sub">
                            {this.t('Visitor')} {visitor_company ? `• ${visitor_company}` : ''}
                        </div>
                    </div>

                    <hr style={{ margin: '0 0 16px 0', borderColor: '#e5e7eb' }} />

                    {isApproved && (
                        <>
                            <div className="vpr-qr-block">
                                {passQrDataUrl && <img src={passQrDataUrl} alt="Visitor Pass QR" width={130} height={130} />}
                                <div className="vpr-ref-num">REF: {refNum}</div>
                            </div>

                            <div className="vpr-valid-dates">
                                <div className="vpr-valid-from">
                                    <div className="vpr-date-label">{this.t('Valid From')}</div>
                                    <div className="vpr-date-value">{shortDateFormat(checkInMoment, dateTimeRules)}</div>
                                    <div className="vpr-date-time">{dateToTimeFormat(checkInMoment, dateTimeRules)}</div>
                                </div>
                                <div className="vpr-valid-until">
                                    <div className="vpr-date-label">{this.t('Valid Until')}</div>
                                    <div className="vpr-date-value">{shortDateFormat(checkOutMoment, dateTimeRules)}</div>
                                    {isMultiDay && (
                                        <div className="vpr-date-time">{dateToTimeFormat(checkOutMoment, dateTimeRules)}</div>
                                    )}
                                </div>
                            </div>

                            <div className="vpr-safety-verified">
                                <i className="fas fa-shield-alt" style={{ color: '#6b7280', fontSize: 11 }} />
                                {this.t('HEALTH & SAFETY COMPLIANCE VERIFIED')}
                            </div>

                            <div className="vpr-instruction">
                                {this.t('Please present this screen to the security officer upon arrival.')}
                            </div>
                        </>
                    )}

                    {status === 'pending' && (
                        <div className="vpr-pending-notice">
                            <i className="fa fa-clock-o" style={{ marginRight: 6 }} />
                            {this.t('Waiting for approval. This page will update automatically when approved.')}
                        </div>
                    )}

                    {isApproved && (
                        <button className="vpr-primary-btn vpr-primary-btn--mb" onClick={this.handleDownloadPublicPass}>
                            <i className="fas fa-download" style={{ fontSize: 13, marginRight: 8 }} />
                            {this.t('Download Pass')}
                        </button>
                    )}

                    <button className="vpr-secondary-btn" onClick={this.resetForm}>
                        <i className="fas fa-redo" style={{ fontSize: 12, marginRight: 7 }} />
                        {this.t('Register Another Visitor')}
                    </button>

                    <div className="vpr-confirm-footer">
                        {this.t('Powered by Time and Control')}
                        <img src={newLogo} alt="Time and Control" height={30} style={{ opacity: 0.7 }} />
                    </div>
                </div>
            </>
        );
    }

    render() {
        const { loading, step, error, zones } = this.state;

        if (error && step === STEP_LANDING && !zones.length && !loading) {
            return this.renderPage(<div className="vpr-loading" style={{ color: '#ef4444' }}>{error}</div>);
        }

        return (
            <>
                {step === STEP_LANDING && this.renderLandingStep()}
                {step === STEP_ZONE && this.renderZoneStep()}
                {step === STEP_FORM && this.renderFormStep()}
                {step === STEP_HEALTH && this.renderHealthStep()}
                {step === STEP_CONFIRM && this.renderConfirmStep()}
            </>
        );
    }
}

export default VisitorPublicRegister;
